"use client";

/**
 * VariantPriceAndQty — client island for the PDP order block (P14a-T13).
 *
 * The PDP page is a server component. `QtyAndAdd` and `VolumeBreakLadder`
 * both need to share the same `qty` state so the ladder can highlight the
 * active tier as the buyer adjusts the stepper. This thin client wrapper:
 *  1. Holds `qty` state, seeded to the variant's MOQ.
 *  2. Renders `QtyAndAdd` (which calls back with the new qty on each change).
 *  3. Renders `VolumeBreakLadder` (which receives `currentQuantity`).
 *
 * `VariantBlock` (in page.tsx) remains a server-component function — it
 * simply drops this island in place of the two separate components that were
 * there before T13.
 */

import { useState } from "react";
import { QtyAndAdd } from "./QtyAndAdd";
import { VolumeBreakLadder } from "./VolumeBreakLadder";
import type { VolumeBreakTier } from "../src/lib/quantity-helpers";

interface Props {
  variantId: string;
  productTitle: string;
  basePriceMinor: number;
  breaks: ReadonlyArray<VolumeBreakTier>;
  minOrderQuantity: number;
  orderIncrement: number;
  disabled?: boolean;
}

export function VariantPriceAndQty({
  variantId,
  productTitle,
  basePriceMinor,
  breaks,
  minOrderQuantity,
  orderIncrement,
  disabled,
}: Props) {
  const [qty, setQty] = useState(Math.max(1, minOrderQuantity));

  return (
    <>
      <VolumeBreakLadder
        basePriceMinor={basePriceMinor}
        breaks={breaks}
        currentQuantity={qty}
      />
      <QtyAndAdd
        variantId={variantId}
        productTitle={productTitle}
        minOrderQuantity={minOrderQuantity}
        orderIncrement={orderIncrement}
        onQuantityChange={setQty}
        {...(disabled ? { disabled: true } : {})}
      />
    </>
  );
}
